import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Fade,
    Flex,
    Heading,
    Skeleton,
    Stack,
    Text,
    useBreakpointValue
} from '@chakra-ui/react'
import DisplayPrice from '../display-price'
import PromoCallout from '../product-tile/promo-callout'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import DynamicImage from '@app/components/dynamic-image'
import {getPriceData} from '../../utils/product-utils'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useShopperBasketsMutationHelper, useProducts} from '@salesforce/commerce-sdk-react'
import {useIntl, FormattedMessage} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '@app/hooks/use-toast'
import {useGTM} from '@app/hooks/use-gtm'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useLocation} from 'react-router-dom'
import Link from '../link'
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'

/**
 * Renders a button group for product interaction within a shop look drawer.
 *
 * Displays a primary "Add to Cart" button and a "More details" link for a specific product.
 * The "Add to Cart" button triggers the provided onClick handler, while the "More details" link
 * navigates to the product's detail page and triggers the onMoreDetailsClick handler.
 *
 * Props:
 * @param {function} onClick - Handler function invoked when the "Add to Cart" button is clicked.
 * @param {string|number} productId - Unique identifier for the product, used to build the product detail URL.
 * @param {function} onMoreDetailsClick - Handler function invoked when the "More details" link is clicked.
 *
 * @returns {JSX.Element} A stack containing the "Add to Cart" button and "More details" link.
 */
const ShopLookDrawerButton = ({onClick, productId, onMoreDetailsClick}) => (
    <Stack textAlign={'center'} spacing={10}>
        <Button key="cart-button" onClick={onClick} variant="primary">
            <FormattedMessage defaultMessage="Add to Cart" id="product_view.button.add_to_cart" />
        </Button>
        <Link onClick={onMoreDetailsClick} to={productUrlBuilder({id: productId})}>
            <FormattedMessage defaultMessage="More details" id="product_view.link.more_details" />
        </Link>
    </Stack>
)

ShopLookDrawerButton.propTypes = {
    onClick: PropTypes.func,
    productId: PropTypes.string,
    onMoreDetailsClick: PropTypes.func
}

const ShopLookDrawerProducts = ({products, onClose, styles, activeCurrency}) => {
    const [showOptionsMessage, setShowOptionsMessage] = useState(false)
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const intl = useIntl()
    const [variant, setVariant] = useState(null)
    const showToast = useToast()
    const location = useLocation()
    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose
    } = useAddToCartModalContext()
    const {triggerAddToCartEvent, triggerViewItemEvent} = useGTM()

    useEffect(() => {
        if (isAddToCartModalOpen) {
            onAddToCartModalClose()
        }
    }, [location.pathname])

    const productIds = products?.map((product) => product.productId || product.id)

    const {data: productSearchResult} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                allImages: true,
                select: '(data.(id, variationAttributes.(**), imageGroups.(**)))'
            }
        },
        {enabled: true}
    )

    if (productSearchResult?.data?.length) {
        products = products.map((product) => {
            const currentProduct = productSearchResult.data.find(
                (productSearchProduct) =>
                    productSearchProduct.id === (product.productId || product.id)
            )
            product.variationAttributes = currentProduct?.variationAttributes
            product.imageGroups = currentProduct?.imageGroups
            return product
        })
    }

    const orderVariantsBySize = (product) => {
        // Mapping sizes to get correct order
        const sizeOrder = product.variationAttributes
            .find((attr) => attr.id === 'size')
            .values.map((value) => value.value)

        // Compare sizes
        function compareVariantsBySize(a, b) {
            return (
                sizeOrder.indexOf(a.variationValues.size) -
                sizeOrder.indexOf(b.variationValues.size)
            )
        }

        // Order variants
        product.variants.sort(compareVariantsBySize)

        return product
    }

    /**
     * Handles the change of a product variant.
     *
     * This function updates the current variant state with the provided variant
     * and ensures that the options message is hidden. It is typically used to
     * respond to user interactions where a different product variant is selected.
     *
     * @param {Object} variant - The new variant object to be set as the current variant.
     */
    const handleChangeVariant = (variant) => {
        setVariant(variant)
        setShowOptionsMessage(false)
    }

    /**
     * Handles the addition of a product to the shopping cart.
     *
     * This asynchronous function attempts to add a specified product variant to the user's
     * shopping cart. If the variant is available, it constructs the necessary product items
     * and items added arrays, and then calls the function to add these items to a new or
     * existing basket. Upon successful addition, it closes the current modal and opens a
     * modal indicating the item has been added to the cart. If an error occurs during this
     * process, it displays an error toast message to the user.
     *
     * If no variant is provided, it sets a flag to show a message indicating that options
     * need to be selected.
     *
     * @async
     * @param {Object} product - The product object to be added to the cart.
     * @param {Object} product.variant - The variant of the product to be added.
     * @param {string} product.variant.productId - The unique identifier for the product variant.
     * @param {number} product.variant.price - The price of the product variant.
     * @param {Function} addItemToNewOrExistingBasket - Function to add items to the basket.
     * @param {Function} setShowOptionsMessage - Function to set the visibility of the options message.
     * @param {Function} onClose - Function to close the current modal.
     * @param {Function} onAddToCartModalOpen - Function to open the add-to-cart modal.
     * @param {Function} showToast - Function to display toast messages.
     * @param {Object} intl - Internationalization object for formatting messages.
     * @param {Object} API_ERROR_MESSAGE - The error message to display in case of API failure.
     */
    const handleCartItem = async (product) => {
        if (variant) {
            const productItems = [
                {
                    productId: variant.productId,
                    price: variant.price,
                    quantity: 1
                }
            ]

            const itemsAdded = [
                {
                    product,
                    variant,
                    quantity: 1
                }
            ]

            try {
                const basketResponse = await addItemToNewOrExistingBasket(productItems)
                setShowOptionsMessage(false)
                onClose()
                onAddToCartModalOpen({
                    product,
                    itemsAdded,
                    selectedQuantity: 1
                })

                // GTM Integration
                const finalProduct = {
                    ...product,
                    ...variant
                }
                finalProduct.ean = variant?.ean || variant?.productId
                triggerAddToCartEvent(basketResponse?.productItems, finalProduct)
            } catch (e) {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }
        } else {
            setShowOptionsMessage(true)
        }
    }

    return (
        <Stack spacing={20}>
            {products.map((product) => {
                if (product.variationAttributes) {
                    product = orderVariantsBySize(product)
                }

                let currentVariationValues = product.variationValues

                if (!currentVariationValues) {
                    currentVariationValues = product.variationGroups.find(
                        (group) => product.productId === group.productId
                    )?.variationValues
                }

                const image =
                    findImageGroupBy(product.imageGroups, {
                        viewType: 'front',
                        selectedVariationAttributes: currentVariationValues
                    })?.images?.[0] || product.image

                const priceData = getPriceData(product)

                return (
                    <Stack spacing={10} key={product.id}>
                        <Flex gap={20}>
                            <Link
                                to={productUrlBuilder({
                                    id: product.id || product.productId
                                })}
                            >
                                {image && (
                                    <DynamicImage
                                        width={isMobile ? '125px' : '180px'}
                                        data-testid="product-tile-image"
                                        src={`${image?.disBaseLink || image?.link}[?sw={width}]`}
                                        widths={[85, 147, 170, 294]}
                                        imageProps={{
                                            alt: image?.alt,
                                            loading: 'lazy'
                                        }}
                                    />
                                )}
                            </Link>
                            <Box>
                                <Heading as={'div'} {...styles.productName}>
                                    <Link
                                        to={productUrlBuilder({
                                            id: product.id || product.productId
                                        })}
                                        textDecoration="none"
                                    >
                                        {product.name || product.productName}
                                    </Link>
                                </Heading>
                                <DisplayPrice
                                    labelForA11y={product?.name}
                                    priceData={priceData}
                                    currency={product?.currency || activeCurrency}
                                    promoCallout={
                                        <Skeleton isLoaded={product}>
                                            <PromoCallout priceData={priceData} product={product} />
                                        </Skeleton>
                                    }
                                    {...styles.productPrice}
                                />
                                <Flex {...styles.sizeButtons} gap={9} flexWrap="wrap">
                                    {product?.variants?.map((item) => {
                                        const buttonStyles =
                                            variant?.productId === item.productId
                                                ? styles.sizeButtonSelected
                                                : styles.sizeButton

                                        return (
                                            <Button
                                                key={item.productId}
                                                disabled={!item.orderable}
                                                role="radio"
                                                tabIndex="0"
                                                onClick={() => handleChangeVariant(item)}
                                                variant={
                                                    variant?.productId === item.productId
                                                        ? 'primary'
                                                        : 'secondary'
                                                }
                                                {...buttonStyles}
                                            >
                                                {item.variationValues.size}
                                            </Button>
                                        )
                                    })}
                                </Flex>
                                {showOptionsMessage && (
                                    <Fade in={true}>
                                        <Text variant={'bodyLarge2'}>
                                            {intl.formatMessage({
                                                defaultMessage:
                                                    'Please select all your options above',
                                                id: 'product_view.message.select_options'
                                            })}
                                        </Text>
                                    </Fade>
                                )}
                                <HideOnMobile>
                                    <ShopLookDrawerButton
                                        onClick={() => handleCartItem(product)}
                                        productId={product.id || product.productId}
                                        onMoreDetailsClick={() =>
                                            triggerViewItemEvent('select_item', product)
                                        }
                                    />
                                </HideOnMobile>
                            </Box>
                        </Flex>
                        <HideOnDesktop>
                            <ShopLookDrawerButton
                                onClick={() => handleCartItem(product)}
                                productId={product.id || product.productId}
                                onMoreDetailsClick={() =>
                                    triggerViewItemEvent('select_item', product)
                                }
                            />
                        </HideOnDesktop>
                    </Stack>
                )
            })}
        </Stack>
    )
}

ShopLookDrawerProducts.propTypes = {
    products: PropTypes.array,
    onClose: PropTypes.func,
    styles: PropTypes.object,
    activeCurrency: PropTypes.string
}

export default ShopLookDrawerProducts
